<template>
  <div class="bg-grey py-5">
    <div class="kws-int-container">
      <div
        class="event-detail"
        v-if="Object.keys(event).length !== 0 && hasEventParam && !error"
      >
        <p
          class="error-box mb-4"
          v-if="alreadyRegistered"
          v-text="translate('lblAlreadyRegistered')"
        ></p>
        <h3
          class="h3 bold mb-4 mr-5"
          v-text="formatDate(event.startDate, formatStartDate)"
        ></h3>
        <div class="title-row d-flex justify-content-between">
          <h1 class="h1" v-text="event.name"></h1>
          <EventCrops :event-crops="event.crops" :is-big="true"></EventCrops>
        </div>
        <div class="event-body">
          <img
            v-if="event.additionalImage"
            class="mb-4"
            :src="event.additionalImage.src"
            :alt="event.additionalImage.alt"
            :srcset="event.additionalImage.srcset"
            :sizes="event.additionalImage.sizes"
          />
          <div class="times d-flex mb-4">
            <div
              class="
                align-content-center
                d-flex
                icon-wrap
                justify-content-center
              "
            >
              <span class="s s-time align-self-center"></span>
            </div>
            <div class="content-wrap">
              <div
                class="start"
                v-text="
                  dateLabelStart
                "
              ></div>
              <div
                class="end"
                v-text="
                  dateLabel('lblEventEnd', event.endDate, 'dddd, MMMM YYYY')
                "
              ></div>
              <div
                class="latest-signup"
                v-if="event.registrationDeadline"
                v-text="
                  dateLabel(
                    'lblRegistrationDeadline',
                    event.registrationDeadline,
                    'DD. MMMM YYYY'
                  )
                "
              ></div>
            </div>
          </div>
          <div class="location d-flex mb-4" v-if="event.locationHref">
            <div
              class="
                align-content-center
                d-flex
                icon-wrap
                justify-content-center
              "
            >
              <span class="s s-location"></span>
            </div>
            <div class="content-wrap d-flex">
              <div class="address align-self-center">
                <a :href="event.locationHref" target="_blank">
                  {{ event.street }},
                  {{ event.postalCode }}
                  {{ event.city }}
                </a>
              </div>
            </div>
          </div>
          <a
            v-if="isRegistrationMandatory || (isMyKwsAccountMandatory && isLoggedIn)"
            :href="registrationHref"
            class="button button--medium button--primary"
            v-text="registrationLinkText"
            :disabled="alreadyRegistered"
          >
          </a>
          <div
            class="trailing leading"
            v-if="event.additionalText"
            v-html="event.additionalText"
          ></div>
          <p
            class="leading"
            v-if="event.description"
          >{{ event.description }}</p>
          <div v-if="isMyKwsAccountMandatory && !isLoggedIn" class="mt-5">
              <div v-html="translations.lblLoginMandatoryHint" class="mb-3"></div>
              <a
                @click.prevent="redirectToLogin"
                v-text="translations.lblLandingPageUrlButton"
                class="button button--medium button--primary"
                >
              </a>
          </div>
        </div>
      </div>
      <p class="error-box" v-else-if="error" v-text="error"></p>
    </div>
    <loading-indicator v-if="loading && !error"></loading-indicator>
  </div>
</template>

<script>
import EventCrops from '@/events/components/event-crops.vue';
import EventRegistrationMixin from './mixins/EventRegistrationMixin';
import LoadingIndicator from '../shared/components/loading-indicator.vue';

export default {
  name: 'EventDetail',
  components: {
    LoadingIndicator,
    EventCrops,
  },
  data: () => ({
    event: {},
    hasEventParam: false,
    error: null,
    loading: false,
    alreadyRegistered: false,
  }),
  props: {
    translations: Object,
    apiUrl: {
      type: String,
      required: true,
    },
    registrationUrl: {
      type: String,
      required: true,
    },
    formatStartDate: {
      type: String,
      required: true,
    },
    landingpageUrl: {
      type: String,
      required: true,
    },
  },
  mixins: [EventRegistrationMixin],
  computed: {
    registrationHref() {
      return `${this.registrationUrl}?campaignID=${this.event.id}`;
    },
    isLoggedIn() {
      return !!window.KWS && !!window.KWS.user;
    },
    dateLabelStart() {
      const lang = document.querySelector('html').getAttribute('lang');
      const startDateString = this.dateLabel('lblEventBegin', this.event.startDate, 'dddd, MMMM YYYY');
      const startTimeString = lang === 'de'
        ? `${new Date(`${this.event.startDate}T${this.event.Start_Time__c}`).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })} Uhr`
        : new Date(`${this.event.startDate}T${this.event.Start_Time__c}`).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
      return this.event.Start_Time__c
        ? `${startDateString}, ${startTimeString}`
        : startDateString;
    },
  },
  methods: {
    redirectToLogin() {
      localStorage.setItem('redirectToolData', JSON.stringify({
        tenant: document.body.dataset.tenant,
        dataRedirecturlAfterLogin: window.location.href,
      }));
      window.location = this.landingpageUrl;
    },
    async getEventsParticipating() {
      return this.getEventData(`${this.apiUrl}/campaign/events/participating/`);
    },
  },
  async created() {
    localStorage.removeItem('redirectToolData');
    if (this.translations) {
      this.addDictionary(this.translations);
    }
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('event')) return;
    this.hasEventParam = true;

    const result = await this.getEventData(
      `${this.apiUrl}/campaign/events/event/baseinfo/${urlParams.get('event')}`,
    );

    await this.initIsEventFullyBooked(this.apiUrl, urlParams.get('event'));

    if (result && result.data) {
      this.event = this.renameObjectKeys(result.data.info);
    }
    this.event = this.getEventWithAdditionalContentFor(this.event);

    if (this.isLoggedIn) {
      const responseParticipating = await this.getEventsParticipating();
      const eventId = urlParams.get('event');

      this.alreadyRegistered = responseParticipating.data.info.Campaigns.some(
        (campaign) => campaign.Id === `${eventId}`,
      );
    }
  },
};
</script>

<style lang="scss">
.title-row {
  flex-wrap: wrap-reverse;
}
.event-body {
  max-width: rem(690px);
  .icon-wrap {
    margin-right: 1.25rem;
    font-size: 1.375rem;
    width: 40px;
  }
}
</style>
